/**
 * Pattern Suggestion Statistics Dashboard
 * =======================================
 * 
 * Displays analytics about pattern library performance:
 * - Overall acceptance rates
 * - Most effective patterns
 * - Suggestions by confidence level
 * - Trend charts
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Check, X, Lightbulb, Target, Award, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';

interface PatternStat {
  pattern_id: string;
  accept_count: number;
  reject_count: number;
  total_decisions: number;
  accept_rate: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  recommended_confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  needs_review: boolean;
}

interface EventStats {
  event_id: string;
  total_patterns: number;
  active_patterns: number;
  disabled_patterns: number;
  total_decisions: number;
  total_accepts: number;
  total_rejects: number;
  overall_accept_rate: number;
  by_confidence: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  pattern_details?: PatternStat[];
}

interface PatternStatsDashboardProps {
  eventId: string;
  onClose?: () => void;
}

export default function PatternStatsDashboard({ eventId, onClose }: PatternStatsDashboardProps) {
  const [stats, setStats] = useState<EventStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/patterns/${eventId}/stats`);
        setStats(response);
      } catch (err) {
        setError('Failed to load pattern statistics');
        console.error('Error fetching pattern stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [eventId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6" data-testid="pattern-stats-dashboard">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6" data-testid="pattern-stats-dashboard">
        <div className="text-center text-red-600">
          <AlertTriangle className="mx-auto mb-2" size={32} />
          <p>{error || 'No statistics available'}</p>
        </div>
      </div>
    );
  }

  const acceptRateColor = stats.overall_accept_rate >= 0.8 
    ? 'text-green-600' 
    : stats.overall_accept_rate >= 0.6 
    ? 'text-yellow-600' 
    : 'text-red-600';

  // Get top performing patterns
  const topPatterns = (stats.pattern_details || [])
    .filter(p => p.total_decisions >= 5)
    .sort((a, b) => b.accept_rate - a.accept_rate)
    .slice(0, 5);

  // Get patterns needing review
  const patternsNeedingReview = (stats.pattern_details || [])
    .filter(p => p.needs_review || p.accept_rate < 0.5);

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-testid="pattern-stats-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="text-white" size={24} />
            <h2 className="text-xl font-bold text-white">Pattern Library Analytics</h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="close-stats-btn"
            >
              <X size={24} />
            </button>
          )}
        </div>
        <p className="text-blue-100 text-sm mt-1">Event: {eventId}</p>
      </div>

      {/* Summary Stats Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Total Patterns */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="text-blue-500" size={20} />
              <span className="text-sm text-gray-600">Total Patterns</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_patterns}</div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.active_patterns} active, {stats.disabled_patterns} disabled
            </div>
          </div>

          {/* Total Decisions */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Check className="text-green-500" size={20} />
              <span className="text-sm text-gray-600">Total Decisions</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_decisions}</div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.total_accepts} accepts, {stats.total_rejects} rejects
            </div>
          </div>

          {/* Accept Rate */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              {stats.overall_accept_rate >= 0.7 ? (
                <TrendingUp className="text-green-500" size={20} />
              ) : (
                <TrendingDown className="text-red-500" size={20} />
              )}
              <span className="text-sm text-gray-600">Accept Rate</span>
            </div>
            <div className={`text-3xl font-bold ${acceptRateColor}`}>
              {(stats.overall_accept_rate * 100).toFixed(1)}%
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
              <div 
                className={`h-full rounded-full ${
                  stats.overall_accept_rate >= 0.8 ? 'bg-green-500' :
                  stats.overall_accept_rate >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${stats.overall_accept_rate * 100}%` }}
              />
            </div>
          </div>

          {/* By Confidence */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Award className="text-purple-500" size={20} />
              <span className="text-sm text-gray-600">By Confidence</span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-green-700">HIGH</span>
                <span className="font-medium">{stats.by_confidence.HIGH}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-yellow-700">MEDIUM</span>
                <span className="font-medium">{stats.by_confidence.MEDIUM}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-orange-700">LOW</span>
                <span className="font-medium">{stats.by_confidence.LOW}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performing Patterns */}
        {topPatterns.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="text-yellow-500" size={20} />
              Top Performing Patterns
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Decisions</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Accept Rate</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {topPatterns.map((pattern, idx) => (
                    <tr key={pattern.pattern_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <span className="text-yellow-500">🥇</span>}
                          {idx === 1 && <span className="text-gray-400">🥈</span>}
                          {idx === 2 && <span className="text-orange-600">🥉</span>}
                          <span className="font-medium text-gray-900">{pattern.pattern_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {pattern.total_decisions}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          pattern.accept_rate >= 0.8 ? 'bg-green-100 text-green-800' :
                          pattern.accept_rate >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(pattern.accept_rate * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          pattern.confidence === 'HIGH' ? 'bg-green-100 text-green-700' :
                          pattern.confidence === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {pattern.confidence}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Patterns Needing Review */}
        {patternsNeedingReview.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={20} />
              Patterns Needing Review
            </h3>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="space-y-2">
                {patternsNeedingReview.slice(0, 5).map(pattern => (
                  <div key={pattern.pattern_id} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-900">{pattern.pattern_id}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{pattern.total_decisions} decisions</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        pattern.accept_rate < 0.5 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {(pattern.accept_rate * 100).toFixed(0)}% accept rate
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {patternsNeedingReview.length > 5 && (
                <p className="text-xs text-orange-600 mt-3">
                  +{patternsNeedingReview.length - 5} more patterns need review
                </p>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {stats.total_decisions === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Lightbulb className="mx-auto mb-3 text-gray-400" size={40} />
            <p className="text-lg font-medium">No decisions yet</p>
            <p className="text-sm mt-1">Pattern statistics will appear after accountants accept or reject suggestions</p>
          </div>
        )}
      </div>
    </div>
  );
}
